// SuperAdminPage.js
import React, { useState, useEffect } from 'react';
import './SuperAdminPage.css';
import UserTable from './UserTable';
import UserCards from './UserCards';
import UserModal from './UserModal';
import axios from 'axios';
import { useCookies } from 'react-cookie';
import toast from 'react-hot-toast';
const SuperAdminPage = () => {
  const [activeTab, setActiveTab] = useState('admins');
  const [users, setUsers] = useState({
    admins: [],
    alumni: [],
    students: []
  });
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('firstName');
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'card'

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  useEffect(() => {
    fetchUsers();
  }, []);

     const [cookie, setCookie] = useCookies(["access_token"])
   const token = cookie.token

     const config = {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          }
        };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/alumni/all`);
      const allUsers = response.data;
      
      const categorized = {
        admins: allUsers.filter(user => user.profileLevel === 0 || user.profileLevel === 1),
        alumni: allUsers.filter(user => user.profileLevel === 2),
        students: allUsers.filter(user => user.profileLevel === 3)
      };
      
      setUsers(categorized);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId, newProfileLevel) => {
    try {
      const response = await axios.put(`${API_URL}/alumni/${userId}`, config
      );

      if (response.status === 200) {
        fetchUsers();
        setShowModal(false);
        alert('User role updated successfully!');
      } else {
        alert('Failed to update user role');
      }
    } catch (error) {
      console.error('Error updating user role:', error);
      alert('Error updating user role');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        const response = await axios.delete(`${API_URL}/alumni/super-admin/${userId}`, config);

        if (response.status === 200) {
          fetchUsers();
          toast.success("User deleted successfully");
          // alert('User deleted successfully!');
        } else {
          alert('Failed to delete user');
        }
      } catch (error) {
        console.error('Error deleting user:', error);
        alert('Error deleting user');
      }
    }
  };

  const openModal = (type, user = null) => {
    setModalType(type);
    setSelectedUser(user);
    setShowModal(true);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedUser(null);
    document.body.style.overflow = 'unset';
  };

  const tabs = [
    { key: 'admins', label: 'Admins & Super Admins', count: users.admins.length, icon: '👑' },
    { key: 'alumni', label: 'Alumni', count: users.alumni.length, icon: '🎓' },
    { key: 'students', label: 'Students', count: users.students.length, icon: '📚' }
  ];

  const getRoleName = (profileLevel) => {
    switch(profileLevel) {
      case 0: return 'Super Admin';
      case 1: return 'Admin';
      case 2: return 'Alumni';
      case 3: return 'Student';
      default: return 'Unknown';
    }
  };

  const getRoleBadgeClass = (profileLevel) => {
    switch(profileLevel) {
      case 0: return 'badge-super-admin';
      case 1: return 'badge-admin';
      case 2: return 'badge-alumni';
      case 3: return 'badge-student';
      default: return 'badge-unknown';
    }
  };

  const filteredUsers = users[activeTab].filter(user =>
    `${user.firstName} ${user.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const sortedUsers = [...filteredUsers].sort((a, b) => {
    if (sortBy === 'firstName') {
      return a.firstName.localeCompare(b.firstName);
    }
    if (sortBy === 'email') {
      return a.email.localeCompare(b.email);
    }
    if (sortBy === 'profileLevel') {
      return a.profileLevel - b.profileLevel;
    }
    return 0;
  });

  return (
    <div className="superadmin-page bg-gradient-to-br from-blue-50 to-indigo-100 min-h-screen">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-8 bg-white/90 backdrop-blur-sm rounded-2xl p-6 border border-gray-200 shadow-xl">
          <div className="mb-4 lg:mb-0">
            <h1 className="text-4xl font-bold text-gray-800 mb-2">Admin Management</h1>
            <p className="text-gray-600 text-lg">Manage all users, roles, and permissions</p>
          </div>
          <div className="flex gap-3">
            <button 
              className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              onClick={() => openModal('add')}
            >
              ➕ Add New User
            </button>
            <div className="flex bg-gray-100 rounded-xl p-1 border">
              <button
                className={`px-4 py-2 rounded-lg transition-all font-medium ${
                  viewMode === 'table' 
                    ? 'bg-white text-blue-600 shadow-sm' 
                    : 'text-gray-600 hover:bg-gray-200'
                }`}
                onClick={() => setViewMode('table')}
              >
                📊 Table
              </button>
              <button
                className={`px-4 py-2 rounded-lg transition-all font-medium ${
                  viewMode === 'card' 
                    ? 'bg-white text-blue-600 shadow-sm' 
                    : 'text-gray-600 hover:bg-gray-200'
                }`}
                onClick={() => setViewMode('card')}
              >
                🎴 Cards
              </button>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex flex-wrap gap-3 mb-8">
          {tabs.map(tab => (
            <button
              key={tab.key}
              className={`px-6 py-4 rounded-xl font-semibold transition-all duration-300 transform hover:-translate-y-1 ${
                activeTab === tab.key 
                  ? 'bg-white text-blue-600 shadow-xl border-2 border-blue-200' 
                  : 'bg-white/70 text-gray-700 hover:bg-white hover:shadow-lg border border-gray-200'
              }`}
              onClick={() => setActiveTab(tab.key)}
            >
              <span className="mr-2">{tab.icon}</span>
              {tab.label} 
              <span className={`ml-2 px-3 py-1 rounded-full text-xs font-bold ${
                activeTab === tab.key 
                  ? 'bg-blue-100 text-blue-800' 
                  : 'bg-gray-100 text-gray-600'
              }`}>
                {tab.count}
              </span>
            </button>
          ))}
        </div>

        {/* Controls */}
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 mb-8 border border-gray-200 shadow-lg">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1">
              <input
                type="text"
                placeholder="🔍 Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-3 bg-gray-50 text-gray-800 placeholder-gray-500 rounded-xl border border-gray-300 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200 transition-all"
              />
            </div>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-3 bg-gray-50 text-gray-800 rounded-xl border border-gray-300 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200 transition-all"
            >
              <option value="firstName">Sort by Name</option>
              <option value="email">Sort by Email</option>
              <option value="profileLevel">Sort by Role</option>
            </select>
          </div>
        </div>

        {/* Content */}
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl border border-gray-200 shadow-xl overflow-hidden">
          {loading ? (
            <div className="flex justify-center items-center py-16">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
              <span className="ml-4 text-gray-700 text-xl">Loading users...</span>
            </div>
          ) : viewMode === 'table' ? (
            <UserTable
              users={sortedUsers}
              onEdit={(user) => openModal('edit', user)}
              onPromote={(user) => openModal('promote', user)}
              onDelete={handleDeleteUser}
              getRoleName={getRoleName}
              getRoleBadgeClass={getRoleBadgeClass}
            />
          ) : (
            <UserCards 
              users={sortedUsers}
              onEdit={(user) => openModal('edit', user)}
              onPromote={(user) => openModal('promote', user)}
              onDelete={handleDeleteUser}
              getRoleName={getRoleName}
              getRoleBadgeClass={getRoleBadgeClass}
            />
          )}
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <UserModal
          type={modalType}
          user={selectedUser}
          onClose={closeModal}
          onSubmit={modalType === 'promote' ? handleRoleChange : fetchUsers}
          getRoleName={getRoleName}
        />
      )}
    </div>
  );
};

export default SuperAdminPage;
